import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { SETTINGS_KEYS, environment } from '../../config/environment'
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination'
import { useSetting } from '../../hooks/useSettings'
import { settingsService } from '../../services/settings.service'
import { toastService } from '../../services/toast.service'
import { trashService } from '../../services/trash.service'
import { Document } from '../../types'
import PageHeader from '../common/PageHeader'
import { Pagination } from '../common/Pagination'
import PreviewPopup, { PreviewPopupRef } from '../common/PreviewPopup'
import './TrashList.scss'

export const TrashList: React.FC = () => {
  const { t } = useTranslation()
  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)
  const [initialLoad, setInitialLoad] = useState(true)
  const [page, setPage] = useState(1)
  const [totalDocuments, setTotalDocuments] = useState(0)
  const [selectedDocuments, setSelectedDocuments] = useState<Set<number>>(new Set())
  const [toggleAll, setToggleAll] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [confirmDialogMessage, setConfirmDialogMessage] = useState('')
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null)
  const previewRefs = useRef<Map<number, PreviewPopupRef>>(new Map())
  const appTitle = useSetting(SETTINGS_KEYS.APP_TITLE, environment.appTitle)

  const pageSize = DEFAULT_PAGE_SIZE

  useEffect(() => {
    document.title = `${t('trash')} - ${appTitle}`
  }, [t, appTitle])

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const response = await trashService.getTrash(page, pageSize)
      setDocuments(response.results)
      setTotalDocuments(response.count)
      setSelectedDocuments(new Set())
      setToggleAll(false)
    } catch (error) {
      toastService.showError(t('error_loading_data'))
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, t])

  useEffect(() => {
    ;(async () => {
      await loadData()
      setInitialLoad(false)
    })()
  }, [loadData])

  const getDaysRemaining = (document: Document): number => {
    const delay = settingsService.get(SETTINGS_KEYS.EMPTY_TRASH_DELAY) || 30
    const deletedAt = new Date((document as any).deleted_at || document.created)
    const diff = new Date().getTime() - deletedAt.getTime()
    const days = Math.ceil(diff / (1000 * 3600 * 24))
    return Math.max(0, delay - days)
  }

  const handleDelete = async (document: Document) => {
    const executeDelete = async () => {
      try {
        await trashService.emptyTrash([document.id])
        toastService.showInfo(t('document_deleted', { title: document.title }))
        loadData()
        setShowConfirmDialog(false)
      } catch (error) {
        toastService.showError(t('error_deleting_document', { title: document.title }))
      }
    }

    setConfirmDialogMessage(
      t('confirm_delete_document', { title: document.title }) || `Da li zaista želite da trajno obrišete dokument "${document.title}"?`
    )
    setConfirmAction(() => executeDelete)
    setShowConfirmDialog(true)
  }

  const handleEmptyTrash = async (documents?: Set<number>) => {
    const executeDelete = async () => {
      try {
        await trashService.emptyTrash(documents ? Array.from(documents) : undefined)
        toastService.showInfo(t('documents_deleted'))
        setToggleAll(false)
        loadData()
        setShowConfirmDialog(false)
      } catch (error) {
        toastService.showError(t('error_deleting_documents'))
      }
    }

    const message = documents ? t('confirm_delete_selected_documents') : t('confirm_delete_all_documents')

    setConfirmDialogMessage(message)
    setConfirmAction(() => executeDelete)
    setShowConfirmDialog(true)
  }

  const handleRestore = async (document: Document) => {
    try {
      await trashService.restoreDocuments([document.id])
      toastService.showInfo(t('document_restored', { title: document.title }))
      loadData()
    } catch (error) {
      toastService.showError(t('error_restoring_document', { title: document.title }))
    }
  }

  const handleRestoreAll = async (documents: Set<number> = new Set()) => {
    try {
      await trashService.restoreDocuments(documents.size > 0 ? Array.from(documents) : [])
      toastService.showInfo(t('documents_restored'))
      setToggleAll(false)
      loadData()
    } catch (error) {
      toastService.showError(t('error_restoring_documents'))
    }
  }

  const handleToggleAll = useCallback(() => {
    setSelectedDocuments(() => {
      if (toggleAll) {
        return new Set()
      }
      return new Set(documents.map((d: Document) => d.id))
    })
    setToggleAll(!toggleAll)
  }, [toggleAll, documents])

  const handleToggleSelected = useCallback((document: Document) => {
    setSelectedDocuments((prev) => {
      const newSelected = new Set(prev)
      if (newSelected.has(document.id)) {
        newSelected.delete(document.id)
      } else {
        newSelected.add(document.id)
      }
      return newSelected
    })
  }, [])

  const clearSelection = () => {
    setToggleAll(false)
    setSelectedDocuments(new Set())
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const totalPages = Math.max(1, Math.ceil(totalDocuments / pageSize))
  const rangeStart = totalDocuments === 0 ? 0 : (page - 1) * pageSize + 1
  const rangeEnd = Math.min(totalDocuments, page * pageSize)
  const shouldShowPagination = totalDocuments > pageSize

  const showInitialSpinner = loading && initialLoad

  if (showInitialSpinner) {
    return (
      <div className="trash-container">
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('loading')}</span>
          </div>
          <p className="mt-2">{t('loading_data')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="trash-container">
      <PageHeader title={t('trash')} info={t('trash_info')} infoLink="trash">
        {selectedDocuments.size > 0 && (
          <Button className="btn btn-sm btn-outline-secondary me-2" onClick={clearSelection}>
            <i className="bi bi-x"></i>&nbsp;{t('clear_selection')}
          </Button>
        )}
        <Button
          variant="outline-primary"
          size="sm"
          className="me-2"
          onClick={() => handleRestoreAll(selectedDocuments)}
          disabled={selectedDocuments.size === 0}
        >
          <i className="bi bi-arrow-counterclockwise"></i>&nbsp;{t('restore_selected')}
        </Button>
        <Button
          variant="outline-danger"
          size="sm"
          className="me-2"
          onClick={() => handleEmptyTrash(selectedDocuments)}
          disabled={selectedDocuments.size === 0}
        >
          <i className="bi bi-trash"></i>&nbsp;{t('delete_selected')}
        </Button>
        <Button variant="outline-danger" size="sm" onClick={() => handleEmptyTrash()} disabled={documents.length === 0}>
          <i className="bi bi-trash"></i>&nbsp;{t('empty_trash')}
        </Button>
      </PageHeader>

      {shouldShowPagination && (
        <div className="row mb-3">
          <div className="col-auto">
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={handlePageChange} alwaysVisible />
          </div>
        </div>
      )}

      <div className="position-relative">
        {loading && !initialLoad && (
          <div className="trash-container__table-overlay">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('loading')}</span>
            </div>
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr>
                <th>
                  <div className="form-check m-0 ms-2 me-n2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="all-documents"
                      checked={toggleAll}
                      onChange={handleToggleAll}
                      disabled={documents.length === 0}
                    />
                    <label className="form-check-label" htmlFor="all-documents"></label>
                  </div>
                </th>
                <th>{t('name')}</th>
                <th className="d-none d-sm-table-cell">{t('remaining')}</th>
                <th className="text-end">{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {documents.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center text-muted py-4">
                    <i className="bi bi-info-circle me-2"></i>
                    {t('no_documents_in_trash')}
                  </td>
                </tr>
              ) : (
                documents.map((document) => (
                  <tr
                    key={document.id}
                    onMouseLeave={() => {
                      const ref = previewRefs.current.get(document.id)
                      if (ref) {
                        ref.close()
                      }
                    }}
                  >
                    <td>
                      <div className="form-check m-0 ms-2 me-n2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`document-${document.id}`}
                          checked={selectedDocuments.has(document.id)}
                          onChange={() => handleToggleSelected(document)}
                        />
                        <label className="form-check-label" htmlFor={`document-${document.id}`}></label>
                      </div>
                    </td>
                    <td>
                      <div className="d-inline-block">
                        <button className="btn btn-link p-0 text-start" onClick={() => handleRestore(document)}>
                          {document.title}
                        </button>
                        <PreviewPopup
                          ref={(ref) => {
                            if (ref) {
                              previewRefs.current.set(document.id, ref)
                            } else {
                              previewRefs.current.delete(document.id)
                            }
                          }}
                          document={document}
                          linkClasses="btn btn-sm btn-link"
                          linkTitle={t('preview_document')}
                        >
                          <i className="bi bi-eye"></i>
                        </PreviewPopup>
                      </div>
                    </td>
                    <td className="d-none d-sm-table-cell">
                      {getDaysRemaining(document)} {t('days_1')}
                    </td>
                    <td className="text-end">
                      <div className="btn-group">
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => handleRestore(document)} title={t('restore')}>
                          <i className="bi bi-arrow-counterclockwise"></i>
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(document)} title={t('delete')}>
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="text-muted small">
          {totalDocuments > 0 ? `${rangeStart} - ${rangeEnd} / ${totalDocuments}` : '0 / 0'}
          {selectedDocuments.size > 0 && (
            <span className="ms-2">
              ({selectedDocuments.size} {t('selected')})
            </span>
          )}
        </div>
        <Pagination currentPage={page} totalPages={totalPages} onPageChange={handlePageChange} alwaysVisible />
      </div>

      {showConfirmDialog && (
        <div className="modal show" style={{ display: 'block' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('confirm_delete')}</h5>
                <button type="button" className="btn-close" onClick={() => setShowConfirmDialog(false)}></button>
              </div>
              <div className="modal-body">{confirmDialogMessage}</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowConfirmDialog(false)}>
                  {t('cancel')}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => {
                    if (confirmAction) {
                      confirmAction()
                    }
                  }}
                >
                  {t('delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
